import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { PlatformCheckbox } from './components/platform-checkbox';
import { useCreateQuery } from './hooks';

import {
  platformOptions,
  SOURCE_PLATFORM_TYPE,
} from '@/entities/public-filter/public-filter-constants';
import { Button } from '@/shared/components/shadcn/button';
import { Form, FormControl, FormField, FormItem } from '@/shared/components/shadcn/form';
import { Input } from '@/shared/components/shadcn/input';
import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';
import { useCallback, useMemo, useState } from 'react';

const formSchema = z.object({
  queryName: z.string().min(1, 'Please enter a brand, product, or topic'),
});

type FormValues = z.infer<typeof formSchema>;

export function QueryCreationPage() {
  const [selectedPlatforms, setSelectedPlatforms] = useState<SOURCE_PLATFORM_TYPE[]>([
    SOURCE_PLATFORM_TYPE.TIKTOK,
    SOURCE_PLATFORM_TYPE.INSTAGRAM,
  ]);
  const { workspaceId } = useWorkspaceId();
  const createQuery = useCreateQuery();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      queryName: '',
    },
  });

  const handleSubmit = async (data: FormValues) => {
    if (!isFormValid) return;
    await createQuery.mutateAsync({
      workspaceId,
      name: data.queryName.trim(),
      platforms: selectedPlatforms,
    });
  };

  const queryNameValue = form.watch('queryName');
  const isFormValid = useMemo(() => queryNameValue.trim().length > 0, [queryNameValue]);

  const handlePlatformChange = useCallback((platform: SOURCE_PLATFORM_TYPE) => {
    setSelectedPlatforms((prev) => {
      const isExist = prev.includes(platform);
      if (isExist) {
        return prev.filter((p) => p !== platform);
      } else {
        return [...prev, platform];
      }
    });
  }, []);

  return (
    <div className="min-h-screen">
      <div className="flex items-center justify-center pt-56">
        <div className="basis-152.5 rounded-lg p-6">
          <div className="mb-6 space-y-1">
            <h1 className="text-primary text-3xl leading-none font-bold">Create new</h1>
          </div>

          <div className="mb-6 space-y-2">
            <h2 className="text-primary text-2xl leading-none font-medium tracking-tight">
              Pick a brand, product, or topic
            </h2>
          </div>

          <div className="mb-6">
            <div className="flex flex-wrap gap-2">
              {platformOptions.map((platform) => (
                <PlatformCheckbox
                  key={platform.value}
                  value={platform.value}
                  checked={selectedPlatforms.includes(platform.value)}
                  disabled={platform.query_disabled}
                  onChange={handlePlatformChange}
                />
              ))}
            </div>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="queryName"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="relative">
                        <Input
                          {...field}
                          placeholder="Enter a brand, product, or any topic"
                          className="h-14 pr-14 text-base!"
                        />
                        <Button
                          type="submit"
                          disabled={!isFormValid || createQuery.isPending}
                          size="icon"
                          className="absolute top-1/2 right-2 size-10 -translate-y-1/2"
                        >
                          <ArrowRight className="size-4" />
                        </Button>
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
}
